import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert, Dropdown, Pagination, Row, Col } from 'react-bootstrap';
import api from '../services/api';
import { DocumentType, DocumentInstance } from '../types';
import { useArea } from '../context/AreaContext';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import MemorialDownloadButtons from '../components/documents/MemorialDownloadButtons';
import { isMemorialDescritivoInstance } from '../utils/memorialDescritivo';

interface DocumentInstanceListResponse {
  items: DocumentInstance[];
  total: number;
  skip: number;
  limit: number;
}

type SortColumn = 'title' | 'status' | 'created_at' | 'updated_at' | 'document_type' | 'creator';

const Documents: React.FC = () => {
  const { user } = useAuth();
  const { currentArea } = useArea();
  const { showAlert, showConfirm } = useModal();
  const navigate = useNavigate();
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentInstances, setDocumentInstances] = useState<DocumentInstance[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedType, setSelectedType] = useState<DocumentType | null>(null);
  const [editingInstance, setEditingInstance] = useState<DocumentInstance | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [error, setError] = useState<string | null>(null);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterTypeId, setFilterTypeId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortColumn>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filterTypeId, filterStatus, currentArea, sortBy, sortOrder]);

  const canViewAllDocuments = user?.role === 'admin' || !!user?.can_view_all_documents;

  const fetchDocumentTypes = async () => {
    try {
      const params: Record<string, unknown> = { is_active: true };
      if (currentArea) {
        params.area_type = currentArea;
      }
      const response = await api.get('/api/documents/types', { params });
      setDocumentTypes(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de documento:', error);
    }
  };

  const fetchDocumentInstances = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterTypeId) params.document_type_id = parseInt(filterTypeId, 10);
      if (filterStatus) params.status = filterStatus;
      if (currentArea) params.area_type = currentArea;

      const response = await api.get<DocumentInstanceListResponse>('/api/documents/instances', { params });
      const data = response.data;
      setDocumentInstances(data.items || []);
      setTotal(data.total ?? 0);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, debouncedSearch, filterTypeId, filterStatus, currentArea]);

  useEffect(() => {
    fetchDocumentTypes();
  }, [currentArea]);

  useEffect(() => {
    fetchDocumentInstances();
  }, [fetchDocumentInstances]);

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortBy !== column) return '';
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleCreate = (type: DocumentType) => {
    setSelectedType(type);
    setEditingInstance(null);
    setFormData({ title: '' });
    setError(null);
    setShowCreateModal(true);
  };

  const handleEdit = (instance: DocumentInstance) => {
    setSelectedType(instance.document_type || null);
    setEditingInstance(instance);
    setFormData({ title: instance.title || '' });
    setError(null);
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!selectedType) return;

    try {
      setError(null);
      if (editingInstance) {
        await api.put(`/api/documents/instances/${editingInstance.id}`, formData);
      } else {
        const response = await api.post('/api/documents/instances', {
          document_type_id: selectedType.id,
          title: formData.title || undefined,
          status: 'draft',
        });
        navigate(`/documents/${response.data.id}/edit`);
        return;
      }
      setShowCreateModal(false);
      fetchDocumentInstances();
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Erro ao salvar documento');
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedType) return;

    try {
      setError(null);
      await api.post('/api/documents/instances', {
        document_type_id: selectedType.id,
        title: formData.title || undefined,
        status: 'draft',
      });
      setShowCreateModal(false);
      fetchDocumentInstances();
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Erro ao salvar rascunho');
    }
  };

  const handleFinalize = async (instance: DocumentInstance) => {
    const ok = await showConfirm({
      title: 'Finalizar documento',
      message: `Tem certeza que deseja finalizar o documento "${instance.title || instance.document_type?.name}"?`,
      confirmLabel: 'Finalizar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;

    try {
      await api.put(`/api/documents/instances/${instance.id}`, {
        status: 'finalized',
      });
      fetchDocumentInstances();
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || error.message || 'Erro ao finalizar documento',
        variant: 'danger',
      });
    }
  };

  const handleDelete = async (instance: DocumentInstance) => {
    const ok = await showConfirm({
      title: 'Excluir documento',
      message: `Tem certeza que deseja excluir o documento "${instance.title || instance.document_type?.name}"?`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;

    try {
      await api.delete(`/api/documents/instances/${instance.id}`);
      fetchDocumentInstances();
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || error.message || 'Erro ao excluir documento',
        variant: 'danger',
      });
    }
  };

  const creatorName = (instance: DocumentInstance) =>
    instance.creator?.name || instance.creator?.email || `Usuário #${instance.created_by}`;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{canViewAllDocuments ? 'Documentos' : 'Meus Documentos'}</h2>
        <Dropdown>
          <Dropdown.Toggle variant="primary">Criar Documento</Dropdown.Toggle>
          <Dropdown.Menu>
            {documentTypes.length === 0 ? (
              <Dropdown.Item disabled>Nenhum tipo de documento disponível</Dropdown.Item>
            ) : (
              documentTypes.map((type) => (
                <Dropdown.Item key={type.id} onClick={() => handleCreate(type)}>
                  {type.name}
                </Dropdown.Item>
              ))
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={4}>
              <Form.Label className="small mb-1">Buscar</Form.Label>
              <Form.Control
                type="search"
                placeholder="Título, nome ou e-mail do criador"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-1">Tipo de documento</Form.Label>
              <Form.Select value={filterTypeId} onChange={(e) => setFilterTypeId(e.target.value)}>
                <option value="">Todos</option>
                {documentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-1">Status</Form.Label>
              <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">Todos</option>
                <option value="draft">Rascunho</option>
                <option value="finalized">Finalizado</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th
                    role="button"
                    onClick={() => handleSort('document_type')}
                    style={{ cursor: 'pointer' }}
                  >
                    Tipo{sortIndicator('document_type')}
                  </th>
                  <th role="button" onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                    Título{sortIndicator('title')}
                  </th>
                  <th role="button" onClick={() => handleSort('creator')} style={{ cursor: 'pointer' }}>
                    Criado por{sortIndicator('creator')}
                  </th>
                  <th role="button" onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    Status{sortIndicator('status')}
                  </th>
                  <th role="button" onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                    Criado em{sortIndicator('created_at')}
                  </th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {documentInstances.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center">
                      Nenhum documento encontrado
                    </td>
                  </tr>
                ) : (
                  documentInstances.map((instance) => (
                    <tr key={instance.id}>
                      <td>{instance.document_type?.name || '-'}</td>
                      <td>{instance.title || '-'}</td>
                      <td>{creatorName(instance)}</td>
                      <td>
                        <Badge bg={instance.status === 'finalized' ? 'success' : 'warning'}>
                          {instance.status === 'finalized' ? 'Finalizado' : 'Rascunho'}
                        </Badge>
                      </td>
                      <td>{new Date(instance.created_at).toLocaleString('pt-BR')}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1 align-items-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigate(`/documents/${instance.id}/edit`)}
                          >
                            Editar
                          </Button>
                          {isMemorialDescritivoInstance(instance) && (
                            <MemorialDownloadButtons instance={instance} />
                          )}
                          {instance.status === 'draft' && (
                            <>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleFinalize(instance)}
                              >
                                Finalizar
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(instance)}
                              >
                                Excluir
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Mostrando {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} de {total}
                </small>
                <Pagination className="mb-0">
                  <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && <Pagination.Ellipsis disabled />}
                        <Pagination.Item active={p === page} onClick={() => setPage(p)}>
                          {p}
                        </Pagination.Item>
                      </React.Fragment>
                    ))}
                  <Pagination.Next
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingInstance ? 'Editar Documento' : 'Criar Documento'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Tipo de Documento</Form.Label>
              <Form.Control type="text" value={selectedType?.name || ''} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Salvar Como</Form.Label>
              <Form.Control
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Digite um título para o documento"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancelar
          </Button>
          {!editingInstance && (
            <Button variant="outline-primary" onClick={handleSaveDraft}>
              Salvar Rascunho
            </Button>
          )}
          <Button variant="primary" onClick={handleSave}>
            {editingInstance ? 'Salvar' : 'Criar e Editar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Documents;
